import { useCallback, useEffect } from 'react';

import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useNotification } from './useNotification';
import { isTestLoginEnabled } from '@/auth/testLogin';
import { userManager } from '@/auth/userManager';

export const useHttpErrorHandler = () => {
  const { showError } = useNotification();
  const { t } = useTranslation('errors');

  const startSignIn = useCallback(() => {
    if (isTestLoginEnabled()) {
      // Drop the expired user and reload — TestLoginGate then shows the form.
      userManager.removeUser().finally(() => window.location.reload());
      return;
    }
    const returnTo = window.location.pathname + window.location.search;
    userManager.signinRedirect({ url_state: returnTo });
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      showError({
        message: t('common.SessionExpired'),
        persist: true,
        action: (
          <Button color="inherit" size="small" onClick={startSignIn}>
            {t('common.SignInAgain', { defaultValue: 'Sign in' })}
          </Button>
        ),
      });
    };

    const handleForbidden = () => {
      showError({ message: t('common.Forbidden') });
    };

    const handleServerError = () => {
      showError({ message: t('common.ServerError') });
    };

    window.addEventListener('api:unauthorized', handleUnauthorized);
    window.addEventListener('api:forbidden', handleForbidden);
    window.addEventListener('api:server-error', handleServerError);
    return () => {
      window.removeEventListener('api:unauthorized', handleUnauthorized);
      window.removeEventListener('api:forbidden', handleForbidden);
      window.removeEventListener('api:server-error', handleServerError);
    };
  }, [showError, startSignIn, t]);
};
